import { MIN_TOOLTIP_WIDTH } from 'components/Charting/Tooltip/ChartTooltip.styled';
import { useDimensions } from 'hooks/useDimensions/useDimensions';
import { useLayoutEffect, useRef, useState } from 'react';

type TooltipPosition = {
  top: number;
  left?: number;
  right?: number;
  visibility: 'visible' | 'hidden';
};

export function useTooltipPosition<T extends HTMLDivElement>(
  marginY: number,
  marginX: number,
) {
  const tooltipRef = useRef<T | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<TooltipPosition>({
    top: 0,
    left: 0,
    right: undefined,
    visibility: 'hidden',
  });

  const { isMobile, viewportWidth } = useDimensions();

  useLayoutEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!tooltipRef.current) {
        return;
      }

      const mouseX = event.pageX;
      const mouseY = event.pageY;

      const parentRect =
        tooltipRef.current.parentElement?.getBoundingClientRect();

      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      if (!parentRect) {
        return;
      }

      const top = mouseY - tooltipRect.height + marginY;
      const right = undefined;
      let left;

      const tooltipWidth =
        tooltipRect.width > 0 ? tooltipRect.width : MIN_TOOLTIP_WIDTH;

      if (isMobile) {
        left = (viewportWidth - tooltipRect.width) / 2;
      } else if (mouseX + marginX + tooltipWidth >= parentRect.right) {
        left = mouseX - marginX - tooltipRect.width;
      } else {
        left = mouseX + marginX;
      }

      const hide = top === 0;

      setTooltipStyle({
        left,
        right,
        top,
        visibility: hide ? 'hidden' : 'visible',
      });
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [marginX, marginY, tooltipRef]);

  return { tooltipRef, tooltipStyle };
}
